import React, { useRef, useEffect, useState } from 'react';
import cx from 'classnames';
import './UpdatePhoneView.scss';
import PropTypes from 'prop-types';
import InfoOutlineIcon from '../../../svg/icons/ic_info-outline_red_35x35.svg';
import ProcessingIcon from '../../../svg/icons/processing-spinner-accessible-red.svg';
import TranslationProvider, { getTranslation } from '../../../providers/TranslationProvider';
import Button from '../../Button';

const NUMBER_OF_DIGITS = 6;
const EMPTY_STATE = new Array(NUMBER_OF_DIGITS).fill('');
// Remove anything not a digit character from incoming typed values, including whitespace.
const sanitizeOtpValue = (value = '') => value.replace(/\D/g, '');
const isSingleDigit = (value = '') => /^\d$/.test(value);

const InputCode = (props) => {
  const {
    processing, onComplete, error, onResend,
  } = props;
  const [otp, setOtp] = useState(EMPTY_STATE);
  const [otpError, setOtpError] = useState(error);
  const otpBoxReference = useRef([]);

  useEffect(() => {
    setOtpError(error);
  }, [error]);

  const onChange = (e, index) => {
    const unsanitizedValue = e.target.value;
    let value = sanitizeOtpValue(unsanitizedValue);
    if (value !== unsanitizedValue) {
      // The user entered a disallowed character, don't let that go into the (controlled) component state;
      // just do an early return.
      return;
    }
    if (value.length > 2) {
      return; // This helps to skip onChange while auto filling in mobile web
    }

    if (value.length === 2) {
      value = value.substring(1); // This rewrites the value if a box is already filled.
    }
    setOtpError(null);
    setOtp(otp.toSpliced(index, 1, value));
  };

  const handleBackspace = (e, index) => {
    if (e.key === 'Backspace' && index > 0) {
      otpBoxReference.current[index - 1].focus();
    }
  };

  const onPaste = (e) => {
    const code = sanitizeOtpValue(e.clipboardData.getData('Text'));
    if (code.length === NUMBER_OF_DIGITS) {
      setOtp(code.split(''));
    } else {
      // Prevent any behavior from the paste operation if the code is not of the expected length,
      // including triggering the onInput() or handleChange()
      e.preventDefault();
    }
  };

  const onInput = (e) => {
    const value = sanitizeOtpValue(e.target.value);
    if (value.length === NUMBER_OF_DIGITS) { // Should consider this fn only when prefilling from messages
      setOtp(value.split(''));
    }
  };

  // Advancing the focus is handled entirely here. Regular onInput (and onChange) is handled
  // in the event capturing phase, earlier, not the bubbling phase.
  const onInputForFocusChange = (e, index) => {
    const typedCharacter = e.nativeEvent?.data;
    if (isSingleDigit(typedCharacter) && index < NUMBER_OF_DIGITS - 1) {
      otpBoxReference.current[index + 1]?.focus();
    }
  };

  const resend = () => {
    setOtp(EMPTY_STATE);
    onResend?.();
  };

  useEffect(() => {
    const updatedOtp = otp.join('');
    if (updatedOtp.length === NUMBER_OF_DIGITS) {
      onComplete(updatedOtp);
    }
  }, [otp]);

  const getInputField = (index, digit) => (
    <input
      key={index}
      value={digit}
      disabled={processing}
      onChangeCapture={(e) => onChange(e, index)}
      onKeyUp={(e) => handleBackspace(e, index)}
      onPaste={onPaste}
      onInputCapture={onInput}
      onInput={(e) => onInputForFocusChange(e, index)}
      ref={(reference) => { otpBoxReference.current[index] = reference; }}
      className={cx('InputCode__Field-cell', { 'InputCode__Field-cell--error': (otpError && !processing) })}
      autoComplete="one-time-code"
      id={`otp-${index}`}
      inputMode="numeric"
      aria-label={getTranslation({ translationKey: 'otp_digit_number', translationValues: { digitNumber: index + 1 }, fallback: `Digit number ${index + 1} in OTP code` })}
      aria-describedby="input_code_error"
      aria-invalid={otpError !== null}
      aria-live="polite"
      autoFocus={index === 0} // eslint-disable-line jsx-a11y/no-autofocus
      pattern="[0-9]*"
    />
  );

  return (
    <div className="InputCode__Container">
      <div className="InputCode__Field-container">
        {otp.map((digit, index) => {
          const field = getInputField(index, digit);
          return ((index) === NUMBER_OF_DIGITS / 2) ? (
            // This case puts a visual divider in the UI at its midpoint
            <>
              <hr className="InputCode__Divider" />
              {field}
            </>
          ) : field;
        })}
      </div>
      {processing && <ProcessingIcon data-testid="processingIcon" />}
      {otpError && (
        <div className="InputCode__Error-container">
          <InfoOutlineIcon className="InputCode__Field--info" />
          <p id="input_code_error" role="alert" aria-live="polite" className={`InputCode__Field-error ${(otpError && !processing) ? 'error-show' : ''}`}>{otpError}</p>
        </div>
      )}
      {!processing && onResend && (
      <TranslationProvider
        translationKey="resend_code"
      >
        <Button
          onClick={resend}
          type="button"
          variant="link"
          id="resend-code-button"
        >
          Resend Code
        </Button>
      </TranslationProvider>
      )}
    </div>
  );
};

InputCode.propTypes = {
  processing: PropTypes.bool,
  error: PropTypes.string,
  onComplete: PropTypes.func.isRequired,
  onResend: PropTypes.func,
};

InputCode.defaultProps = {
  processing: false,
  error: undefined,
  onResend: undefined,
};

export default InputCode;
